import React, { useEffect, useState } from "react";
import { Clock, ChefHat, CheckCircle2, Bike, ShoppingBag, Utensils } from "lucide-react";
import OrderDetailDialog from "../OrderDetailDialog";

export const SLA = { green: "#2E7D32", amber: "#D97706", red: "#C62828" };
export function slaColor(mins, station) {
  const g = station?.sla_green ?? 10, a = station?.sla_amber ?? 20;
  return mins <= g ? SLA.green : mins <= a ? SLA.amber : SLA.red;
}
export function slaLabel(mins, station, status) {
  if (status === "ready") return "Ready";
  const g = station?.sla_green ?? 10, a = station?.sla_amber ?? 20;
  return mins <= g ? "On Time" : mins <= a ? "Warning" : "Overdue";
}
export const elapsedSec = (d) => Math.max(0, Math.floor((Date.now() - new Date(d).getTime()) / 1000));
export const fmtElapsed = (s) => {
  const m = Math.floor(s / 60);
  return m >= 100 ? `${m}m` : `${String(m).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
};

const TYPE_ICON = { dine_in: Utensils, takeaway: ShoppingBag, delivery: Bike, corporate: Utensils };
const TYPE_LABEL = { dine_in: "DINE IN", takeaway: "TAKEAWAY", delivery: "DELIVERY", corporate: "CORPORATE" };
const NEXT = {
  received:    ["in_progress", "START PREPARING", "#8B1E1E", "#6E1414", ChefHat],
  in_progress: ["ready",       "MARK AS READY",   "#D97706", "#B45309", CheckCircle2],
  ready:       ["completed",   "COMPLETED",       "#2E7D32", "#1B5E20", CheckCircle2],
};

export default function TicketCard({ ticket, station, stationMeta, onAdvance, busy }) {
  const [, tick] = useState(0);
  const [showDetail, setShowDetail] = useState(false);
  useEffect(() => { const t = setInterval(() => tick((x) => x + 1), 1000); return () => clearInterval(t); }, []);
  const secs = elapsedSec(ticket.created_at);
  const color = slaColor(secs / 60, stationMeta);
  const label = slaLabel(secs / 60, stationMeta, ticket.status);
  const [next, btnText, bg, bgHover, Icon] = NEXT[ticket.status] || [];
  const TIcon = TYPE_ICON[ticket.order_type] || Utensils;
  const placed = new Date(ticket.created_at).toLocaleTimeString("en-NG", { hour: "2-digit", minute: "2-digit" });

  return (
    <div
      data-testid={`kds-ticket-${ticket.id}`}
      className="rounded-lg bg-white border flex flex-col overflow-hidden transition-shadow hover:shadow-md"
      style={{ borderColor: "#EFE4D0", boxShadow: "0 1px 2px rgba(59,39,34,0.04)" }}
    >
      <div className="px-4 pt-3 pb-2 flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-brand font-bold text-lg leading-none tracking-tight" style={{ color: "#3B2722" }}>
            #{ticket.order_number}
          </div>
          <div className="text-[11px] uppercase tracking-wider mt-2 flex items-center gap-1.5 font-semibold" style={{ color: "#8B7355" }}>
            <TIcon size={12} strokeWidth={2} />
            <span>{TYPE_LABEL[ticket.order_type] || (ticket.order_type || "").toUpperCase()}</span>
            {ticket.table && <><span style={{ color: "#C9BCA6" }}>•</span><span>Table {ticket.table}</span></>}
          </div>
          <div className="text-[11px] mt-0.5" style={{ color: "#A58F7C" }}>{placed}</div>
        </div>
        <div className="shrink-0 text-right leading-tight" data-testid={`kds-timer-${ticket.id}`}>
          <div className="font-brand font-bold text-lg tabular-nums flex items-center gap-1 justify-end" style={{ color }}>
            <Clock size={13} strokeWidth={2.5} />
            {fmtElapsed(secs)}
          </div>
          <div className="text-[11px] font-semibold mt-0.5" style={{ color }}>{label}</div>
        </div>
      </div>

      <div className="px-4 pb-3 flex-1 space-y-1.5">
        {ticket.items.map((it, i) => (
          <div key={i} className="flex gap-3 leading-tight">
            <div className="font-bold text-sm min-w-[1rem] shrink-0 tabular-nums" style={{ color: "#3B2722" }}>
              {Number(it.quantity)}
            </div>
            <div className="min-w-0">
              <div className="font-semibold text-sm" style={{ color: "#3B2722" }}>
                {it.name}
                {!station && ticket.station_codes?.length > 1 && (
                  <span className="ml-1.5 text-[9px] font-bold uppercase tracking-wider px-1.5 py-px rounded" style={{ background: "#F5EDDC", color: "#8B7355" }}>
                    {it.station}
                  </span>
                )}
              </div>
              {(it.size || (it.modifiers || []).length > 0) && (
                <div className="text-[12px]" style={{ color: "#8B7355" }}>
                  {[it.size, ...(it.modifiers || []).map((m) => m.name)].filter(Boolean).join(" • ")}
                </div>
              )}
              {it.notes && <div className="text-[11px] italic" style={{ color: "#A65C2C" }}>"{it.notes}"</div>}
            </div>
          </div>
        ))}
      </div>

      <div className="px-4 pb-2">
        <button data-testid={`kds-detail-${ticket.id}`} onClick={() => setShowDetail(true)}
          className="text-[11px] font-semibold underline" style={{ color: "#8B1E1E" }}>
          Full order details
        </button>
      </div>

      {next && (
        <div className="px-3 pb-3">
          <button
            data-testid={`kds-advance-${ticket.id}`}
            disabled={busy}
            onClick={() => onAdvance(ticket, next)}
            onMouseEnter={(e) => (e.currentTarget.style.background = bgHover)}
            onMouseLeave={(e) => (e.currentTarget.style.background = bg)}
            className="w-full h-11 rounded-md text-white font-bold text-[11px] uppercase tracking-widest active:scale-[0.99] transition-all disabled:opacity-60 flex items-center justify-center gap-2"
            style={{ background: bg }}
          >
            <Icon size={14} strokeWidth={2.5} /> {btnText}
          </button>
        </div>
      )}
      <OrderDetailDialog open={showDetail} orderId={ticket.order_id} onClose={() => setShowDetail(false)} />
    </div>
  );
}
